import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BOOKING_STATUS_LABELS, BookingStatus, TRIP_TYPE_LABELS } from '@fleet/shared';
import { bookingsApi, type MyBooking } from '../api/bookings';
import { formatRange } from '../calendar/calendar-geometry';
import { RescheduleDialog } from './RescheduleDialog';

export function MyBookings() {
  const queryClient = useQueryClient();
  const [rescheduling, setRescheduling] = useState<MyBooking | null>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['my-bookings'],
    queryFn: () => bookingsApi.mine(),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => bookingsApi.cancel(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
    },
  });

  if (isPending) return <p className="text-sm text-slate-500">Wird geladen …</p>;
  if (isError) {
    return (
      <p className="text-sm text-red-700">
        Buchungen konnten nicht geladen werden: {(error as Error).message}
      </p>
    );
  }

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-sm text-slate-500">
          Keine anstehenden Buchungen. Im Kalender kannst du über einen freien Bereich ziehen, um
          ein Fahrzeug zu buchen.
        </p>
      </div>
    );
  }

  return (
    <>
      <ul className="space-y-3">
        {data.map((booking) => (
          <BookingRow
            key={booking.id}
            booking={booking}
            onCancel={() => cancel.mutate(booking.id)}
            onReschedule={() => setRescheduling(booking)}
            isCancelling={cancel.isPending && cancel.variables === booking.id}
          />
        ))}
      </ul>

      {rescheduling && (
        <RescheduleDialog booking={rescheduling} onClose={() => setRescheduling(null)} />
      )}
    </>
  );
}

function BookingRow({
  booking,
  onCancel,
  onReschedule,
  isCancelling,
}: {
  booking: MyBooking;
  onCancel: () => void;
  onReschedule: () => void;
  isCancelling: boolean;
}) {
  const start = new Date(booking.startsAt);
  const end = new Date(booking.endsAt);
  const canCancel =
    booking.status !== BookingStatus.CANCELLED &&
    booking.status !== BookingStatus.COMPLETED &&
    booking.status !== BookingStatus.REJECTED;

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: booking.vehicle.colorHex }}
              aria-hidden
            />
            <span className="font-medium">{booking.vehicle.licensePlate}</span>
            <span className="text-sm text-slate-500">{booking.vehicle.displayName}</span>
            <StatusBadge status={booking.status} />
          </div>

          <p className="mt-1.5 text-sm tabular-nums text-slate-600">{formatRange(start, end)}</p>

          <p className="mt-1 text-sm text-slate-500">
            {TRIP_TYPE_LABELS[booking.tripType]}
            {booking.purpose && ` · ${booking.purpose}`}
          </p>

          <p className="mt-1 font-mono text-xs text-slate-400">{booking.bookingNumber}</p>

          {booking.status === BookingStatus.REJECTED && booking.rejectionReason && (
            <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
              <span className="font-medium">Abgelehnt:</span> {booking.rejectionReason}
            </p>
          )}

          {booking.status === BookingStatus.PENDING_APPROVAL && (
            <p className="mt-2 text-xs text-amber-700">
              Wartet auf Freigabe. Das Fahrzeug ist für diesen Zeitraum bereits für dich reserviert.
            </p>
          )}
        </div>

        {canCancel && (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={onReschedule}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
              title={
                booking.status === BookingStatus.PENDING_APPROVAL
                  ? 'Zeiten frei verschieben, solange nicht freigegeben'
                  : 'Freigegebene Buchungen lassen sich nur verkürzen'
              }
            >
              Zeiten ändern
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isCancelling}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {isCancelling ? 'Wird storniert …' : 'Stornieren'}
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: BookingStatus }) {
  const styles: Record<string, string> = {
    [BookingStatus.PENDING_APPROVAL]: 'bg-amber-100 text-amber-900',
    [BookingStatus.APPROVED]: 'bg-emerald-100 text-emerald-900',
    [BookingStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-900',
    [BookingStatus.COMPLETED]: 'bg-slate-100 text-slate-600',
    [BookingStatus.REJECTED]: 'bg-red-100 text-red-900',
    [BookingStatus.CANCELLED]: 'bg-slate-100 text-slate-500',
  };

  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${styles[status] ?? ''}`}>
      {BOOKING_STATUS_LABELS[status]}
    </span>
  );
}
